import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory } from 'react-router-dom'
import { FrontendSettings } from '../../../helpers/FrontendSettings'
import { useDebounce } from '../../../hooks/useDebounce'
import { useGetTenantsQuery, useLazySearchUsersQuery } from '../../../redux/api/customerApi'
import { useAppSelector } from '../../../redux/store'
import { FrontendSettingsKeys } from '../../../types/enums'
import './UserManagement.scss'
import './UserManagementFunctional.scss'

export default function UserManagementFunctional() {
  const { t } = useTranslation('common')
  const history = useHistory()
  const currentUser = useAppSelector((s) => s.appData.currentUser)

  const [displayQuery, setDisplayQuery] = useState('')
  const debouncedQuery = useDebounce(displayQuery, 2000)

  const { data: tenants = [] } = useGetTenantsQuery()
  const [searchUsers, searchResult] = useLazySearchUsersQuery()

  useEffect(() => {
    FrontendSettings.clear(FrontendSettingsKeys.preselectedTenantId)
  }, [])

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      searchUsers(debouncedQuery)
    }
  }, [debouncedQuery, searchUsers])

  const tenantMap = useMemo<Record<string, string>>(
    () => Object.fromEntries(tenants.map((t) => [t.id, t.name])),
    [tenants]
  )

  const sortedUsers = useMemo(() => {
    const raw = searchResult.data ?? []
    return [...raw].sort((a, b) => {
      if (a.displayName === b.displayName) {
        const tenantA = tenantMap[a.tenantId ?? ''] ?? ''
        const tenantB = tenantMap[b.tenantId ?? ''] ?? ''
        return tenantA > tenantB ? -1 : 1
      }
      return (a.displayName ?? '') < (b.displayName ?? '') ? -1 : 1
    })
  }, [searchResult.data, tenantMap])

  const showResults = sortedUsers.length > 0 && displayQuery.length >= 3
  const showTenantsAdd = currentUser?.isAdmin === true
  const showUsersAdd =
    currentUser?.isAdmin === true || (currentUser?.isInternalUser === true && currentUser?.isManager === true)

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayQuery(e.target.value)
  }, [])

  const handleClear = useCallback(() => {
    setDisplayQuery('')
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (displayQuery.length >= 3) searchUsers(displayQuery)
        return
      }
      if (e.key === 'Backspace' && displayQuery.length === 0) {
        e.preventDefault()
      }
    },
    [displayQuery, searchUsers]
  )

  const handleMoveToUser = useCallback(
    (tenantId: string, userId: string) => {
      FrontendSettings.set(FrontendSettingsKeys.preselectedTenantId, tenantId)
      history.push(`/UserManagement/users/newUserEdit/${tenantId}/${userId}`)
    },
    [history]
  )

  return (
    <div className="table adminArea">
      <div className="table-cell">
        <div className="searchArea">
          <div className="box boxArea searchBar">
            <input
              name={`fieldnameveryunique${new Date().getTime()}`}
              autoComplete="none"
              list="autocompleteOff"
              type="search"
              value={displayQuery}
              placeholder={t('SEARCH_BY_NAME_OR_EMAIL')}
              onChange={handleQueryChange}
              onKeyDown={handleKeyDown}
            />
            {!!displayQuery && <i onClick={handleClear} className="icon-clear" />}
            <i className="icon-search" />
          </div>

          {showResults && (
            <div className="userManagement__searchResultArea">
              {sortedUsers.map((el, i) => (
                <div
                  key={i}
                  onClick={() => (el.tenantId && el.id ? handleMoveToUser(el.tenantId, el.id) : undefined)}
                  className="userManagement__searchResult"
                >
                  <div className="userManagement__resultName">
                    <i className="icon-users" />
                    {el.displayName}
                  </div>
                  <div className="userManagement__resultName">
                    <i className="icon-groups" />
                    {tenantMap[el.tenantId ?? ''] ?? ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex boxArea">
          <div className="box">
            <NavLink to="/UserManagement/groups">
              <i className="icon-groups" />
              {t('TENANTS')}
            </NavLink>
            {showTenantsAdd && (
              <NavLink to="/UserManagement/groups/group/edit/new">
                <i className="icon-add" />
              </NavLink>
            )}
          </div>
          <div className="box">
            <NavLink to="/UserManagement/companies">
              <i className="icon-customers" />
              {t('COMPANIES')}
            </NavLink>
          </div>
          <div className="box">
            <NavLink to="/UserManagement/users">
              <i className="icon-users" />
              {t('USERS')}
            </NavLink>
            {showUsersAdd && (
              <NavLink to="/UserManagement/users/newUserEdit/new">
                <i className="icon-add" />
              </NavLink>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
